import type { RevisionPose } from "./editRevision";
import type { UnitySender } from "./editorBridgePort";
import { areLogicalProjectIdentitiesEquivalent } from "./projectLoad";
import { notifyRevisionAutoSaveMutation } from "./revisionAutoSaveRuntime";

export const EDIT_SNAPSHOT_CONTRACT = "viewr-edit-snapshot-v1";
export type CompleteEditSnapshot = Readonly<{
  contract: typeof EDIT_SNAPSHOT_CONTRACT;
  snapshotId: string;
  transactionId: string;
  runId: number;
  projectPath: string;
  json: string;
  jsonHash: string;
  spacesCount: number;
  currentSpaceIndex: number;
  images: ReadonlyArray<Readonly<{ key: string; base64: string; hash: string }>>;
  /**
   * 요청 프레임 확정(STUDIO-PACKAGE-001 · 2026-09-24)에서 대기 상한까지 오지 않았거나 Unity가 버린 이미지 key.
   * 이 key에는 이전 Revision ref를 쓰지 않는다(Replace 뒤 옛 이미지 저장 방지).
   */
  pendingImageKeys?: ReadonlyArray<string>;
}>;
export const EDIT_POSE_DELTA_CONTRACT = "viewr-edit-pose-delta-v1";
export type EditPoseDelta = Readonly<{
  contract: typeof EDIT_POSE_DELTA_CONTRACT;
  snapshotId: string; transactionId: string; runId: number; projectPath: string;
  baseRevisionId: string; poses: ReadonlyArray<RevisionPose>;
}>;
export type EditingSnapshot = CompleteEditSnapshot | EditPoseDelta;
type SnapshotImage = CompleteEditSnapshot["images"][number];
type CaptureRequest = {
  snapshotId: string; transactionId: string; runId: number;
  projectPath: string; rebindProjectPath?: string; reason?: string; signal: AbortSignal;
  /**
   * "react": 이미지 asset ref를 React가 조립한다(PACKAGE-001 이미지 자산 정책). Unity는 이미지를
   * 수집만 하고 HMI·Comment bundle invariant·barrier로 snapshot을 거부하지 않는다. 누락 판정은 React의 동적 등급.
   */
  imageOwnership?: "react";
  /** Unity가 snapshot을 요청 프레임에 확정하고 렌더 이미지를 `editSnapshotImages`로 뒤따라 보낸다. */
  deferRenderedImages?: boolean;
  allowPoseDelta?: boolean;
  deltaBaseRevisionId?: string;
  committedSnapshotId?: string;
};
type PendingCapture = CaptureRequest & {
  resolve: (snapshot: EditingSnapshot) => void;
  reject: (error: Error) => void;
  /** 확정 snapshot을 받고 뒤따를 이미지를 기다리는 중. */
  awaiting?: { snapshot: CompleteEditSnapshot; outstanding: Set<string>; dropped: Set<string>; timer: ReturnType<typeof setTimeout> };
};
const pending = new Map<string, PendingCapture>();

/** 뒤따를 이미지 대기 상한. 이전 흐름의 이미지 수집 시간과 같은 범위라 Saved 시점이 달라지지 않는다. */
export const EDIT_SNAPSHOT_IMAGE_WAIT_MS = 10_000;
/** 늦은 이미지 재저장은 현재 세션에서만 제한된 시간/개수로 추적한다. */
const LATE_IMAGE_RETENTION_MS = 5 * 60_000;
const MAX_LATE_IMAGE_SNAPSHOTS = 128;
const lateImageSnapshots = new Map<string, {
  transactionId: string; runId: number; expiresAt: number; timer: ReturnType<typeof setTimeout>;
}>();

const forgetLateImageSnapshot = (snapshotId: string): void => {
  const entry = lateImageSnapshots.get(snapshotId);
  if (!entry) return;
  clearTimeout(entry.timer);
  lateImageSnapshots.delete(snapshotId);
};

// Destructure identity so the expiry callback cannot retain the snapshot/image payload or AbortSignal.
const rememberLateImageSnapshot = ({ snapshotId, transactionId, runId }: CaptureRequest): void => {
  forgetLateImageSnapshot(snapshotId);
  if (lateImageSnapshots.size >= MAX_LATE_IMAGE_SNAPSHOTS) {
    forgetLateImageSnapshot(lateImageSnapshots.keys().next().value!);
  }
  lateImageSnapshots.set(snapshotId, {
    transactionId, runId, expiresAt: Date.now() + LATE_IMAGE_RETENTION_MS,
    timer: setTimeout(() => forgetLateImageSnapshot(snapshotId), LATE_IMAGE_RETENTION_MS),
  });
};

const isSnapshotImage = (value: unknown): value is SnapshotImage => {
  const image = value as Record<string, unknown> | null;
  return !!image && typeof image.key === "string" && !!image.key &&
    typeof image.base64 === "string" && !!image.base64 && typeof image.hash === "string" && /^[a-f0-9]{64}$/.test(image.hash);
};

const freezeSnapshot = (snapshot: CompleteEditSnapshot): CompleteEditSnapshot => {
  snapshot.images.forEach(Object.freeze);
  Object.freeze(snapshot.images);
  if (snapshot.pendingImageKeys) Object.freeze(snapshot.pendingImageKeys);
  return Object.freeze(snapshot);
};

const finishAwaiting = (request: PendingCapture): void => {
  const awaiting = request.awaiting;
  if (!awaiting) return;
  clearTimeout(awaiting.timer);
  const missing = Array.from(awaiting.outstanding).concat(Array.from(awaiting.dropped));
  if (awaiting.outstanding.size > 0) rememberLateImageSnapshot(request);
  request.resolve(freezeSnapshot({ ...awaiting.snapshot, ...(missing.length > 0 ? { pendingImageKeys: missing } : {}) }));
};

/** Called after the normal Unity transport source/transaction guards. */
export function receiveCompleteEditSnapshot(value: unknown): void {
  if (!value || typeof value !== "object") return;
  const response = value as Record<string, any>;
  const request = pending.get(response.snapshotId);
  if (!request || request.signal.aborted || response.transactionId !== request.transactionId || response.runId !== request.runId) return;
  if (response.success !== true) {
    request.reject(new Error(typeof response.error === "string" ? response.error : "Editing snapshot capture failed."));
    return;
  }
  if (response.snapshot?.contract === EDIT_POSE_DELTA_CONTRACT) {
    const delta = response.snapshot as EditPoseDelta;
    if (!request.allowPoseDelta || !request.deltaBaseRevisionId || delta.baseRevisionId !== request.deltaBaseRevisionId ||
        delta.snapshotId !== request.snapshotId || delta.transactionId !== request.transactionId || delta.runId !== request.runId ||
        !areLogicalProjectIdentitiesEquivalent(delta.projectPath, request.projectPath) ||
        !Array.isArray(delta.poses) || delta.poses.length < 1 || delta.poses.length > 256) {
      request.reject(new Error("Editing pose delta identity/data is invalid."));
      return;
    }
    request.resolve(Object.freeze(JSON.parse(JSON.stringify(delta)) as EditPoseDelta));
    return;
  }
  const snapshot = response.snapshot as CompleteEditSnapshot | undefined;
  if (!snapshot || snapshot.contract !== EDIT_SNAPSHOT_CONTRACT || snapshot.snapshotId !== request.snapshotId ||
      snapshot.transactionId !== request.transactionId || snapshot.runId !== request.runId ||
      !areLogicalProjectIdentitiesEquivalent(snapshot.projectPath, request.projectPath) ||
      typeof snapshot.json !== "string" || !snapshot.json || !/^[a-f0-9]{64}$/.test(snapshot.jsonHash) ||
      !Number.isInteger(snapshot.spacesCount) || snapshot.spacesCount < 1 || !Array.isArray(snapshot.images)) {
    request.reject(new Error("Complete editing snapshot identity/data is invalid."));
    return;
  }
  // The Engine output is opaque. UI projections never fill or replace it.
  let detached: CompleteEditSnapshot;
  try {
    detached = JSON.parse(JSON.stringify(snapshot)) as CompleteEditSnapshot;
  } catch {
    request.reject(new Error("Complete editing snapshot could not be retained."));
    return;
  }
  const pendingKeys = request.deferRenderedImages && Array.isArray(response.pendingImageKeys)
    ? response.pendingImageKeys.filter((key: unknown): key is string => typeof key === "string" && !!key)
    : [];
  if (pendingKeys.length === 0) {
    request.resolve(freezeSnapshot(detached));
    return;
  }
  request.awaiting = {
    snapshot: detached, outstanding: new Set(pendingKeys), dropped: new Set(),
    timer: setTimeout(() => finishAwaiting(request), EDIT_SNAPSHOT_IMAGE_WAIT_MS),
  };
}

/** 요청 프레임 snapshot의 대기 이미지. 같은 snapshot에 합쳐 한 번에 저장하게 한다. */
export function receiveEditSnapshotImages(value: unknown): void {
  if (!value || typeof value !== "object") return;
  const response = value as Record<string, any>;
  const images = Array.isArray(response.images) ? response.images.filter(isSnapshotImage) : [];
  const request = pending.get(response.snapshotId);
  if (!request?.awaiting || response.transactionId !== request.transactionId || response.runId !== request.runId) {
    // 상한 뒤 이미지 없이 저장한 snapshot의 이미지다. 다음 자동 저장이 Unity에서 새로 받아 채운다.
    const late = lateImageSnapshots.get(response.snapshotId);
    if (!late) return;
    if (Date.now() >= late.expiresAt) { forgetLateImageSnapshot(response.snapshotId); return; }
    if (response.transactionId !== late.transactionId || response.runId !== late.runId) return;
    forgetLateImageSnapshot(response.snapshotId);
    if (images.length > 0) notifyRevisionAutoSaveMutation(-1);
    return;
  }
  const { snapshot, outstanding, dropped } = request.awaiting;
  const byKey = new Map(snapshot.images.map((image) => [image.key, image] as const));
  for (const image of images) {
    if (!outstanding.delete(image.key)) continue;
    byKey.set(image.key, { key: image.key, base64: image.base64, hash: image.hash });
  }
  for (const key of Array.isArray(response.droppedKeys) ? response.droppedKeys : []) {
    if (typeof key === "string" && outstanding.delete(key)) dropped.add(key);
  }
  request.awaiting.snapshot = { ...snapshot, images: Array.from(byKey.values()) };
  if (outstanding.size === 0) finishAwaiting(request);
}

/** capture가 timeout으로 끝날 때의 문구. 소비자가 일시 실패로 분류하는 근거라 상수로 고정한다. */
export const EDIT_SNAPSHOT_CAPTURE_TIMEOUT_ERROR = "Editing snapshot capture timed out; runtime was preserved.";

/** Register before sending: Unity can answer synchronously. No Save/API side effects. */
export function requestCompleteEditSnapshot(send: UnitySender, request: CaptureRequest & { allowPoseDelta: true }, timeoutMs?: number): Promise<EditingSnapshot>;
export function requestCompleteEditSnapshot(send: UnitySender, request: CaptureRequest, timeoutMs?: number): Promise<CompleteEditSnapshot>;
export function requestCompleteEditSnapshot(send: UnitySender, request: CaptureRequest, timeoutMs = 30000): Promise<EditingSnapshot> {
  return new Promise((resolve, reject) => {
    if (request.signal.aborted) { reject(new Error("Editing snapshot capture cancelled.")); return; }
    if (pending.has(request.snapshotId)) { reject(new Error("Editing snapshot capture already pending.")); return; }
    forgetLateImageSnapshot(request.snapshotId);
    let settled = false;
    const cleanup = () => {
      if (settled) return false;
      settled = true;
      clearTimeout(timeout);
      const entry = pending.get(request.snapshotId);
      if (entry?.awaiting) clearTimeout(entry.awaiting.timer);
      request.signal.removeEventListener("abort", abort);
      pending.delete(request.snapshotId);
      return true;
    };
    const fail = (error: Error) => { if (cleanup()) reject(error); };
    const abort = () => fail(new Error("Editing snapshot capture cancelled."));
    const timeout = setTimeout(() => fail(new Error(EDIT_SNAPSHOT_CAPTURE_TIMEOUT_ERROR)), timeoutMs);
    pending.set(request.snapshotId, { ...request,
      resolve: (snapshot) => { if (cleanup()) resolve(snapshot); }, reject: fail,
    });
    request.signal.addEventListener("abort", abort, { once: true });
    try { void send("reactToUnity_captureEditSnapshot", {
      snapshotId: request.snapshotId, transactionId: request.transactionId, runId: request.runId,
      viewrGCSPath: request.rebindProjectPath, reason: request.reason,
      ...(request.imageOwnership ? { imageOwnership: request.imageOwnership } : {}),
      ...(request.deferRenderedImages ? { deferRenderedImages: true } : {}),
      ...(request.allowPoseDelta ? { allowPoseDelta: true, deltaBaseRevisionId: request.deltaBaseRevisionId,
        committedSnapshotId: request.committedSnapshotId } : {}),
    }, {
      transactionId: request.transactionId, dedupeKey: `capture-edit:${request.snapshotId}`, requiresReadyState: "handlers_ready",
    }).catch(fail); } catch (error) { fail(error instanceof Error ? error : new Error(String(error))); }
  });
}

export const EDIT_SNAPSHOT_CAPTURE_SESSION_ENDED_ERROR = "Editing snapshot capture cancelled; editor session ended.";

/**
 * Editor 세션이 끝날 때 대기 중인 capture를 전부 끝낸다. 각 요청의 timeout(기본 30초)도
 * 함께 해제되므로 Home으로 나간 뒤 이전 세션의 timeout이 뒤늦게 `Save failed`를 만들지
 * 않는다. 호출자가 넘기는 signal은 일회용이라 밖에서 abort할 수 없어 여기서 직접 끝낸다.
 */
export function cancelPendingEditSnapshotCaptures(reason = EDIT_SNAPSHOT_CAPTURE_SESSION_ENDED_ERROR): number {
  lateImageSnapshots.forEach((_entry, snapshotId) => forgetLateImageSnapshot(snapshotId));
  const captures = Array.from(pending.values());
  captures.forEach((capture) => capture.reject(new Error(reason)));
  return captures.length;
}
